import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { menuData, tabs } from '../data/menuData';

/* ─── Hero Slide Data ─── */
const heroSlides = [
  {
    image: '/images/hero-coffee.png',
    tagline: 'Crafted with Passion',
    title: 'Premium Coffee',
    titleAccent: 'Experience',
    description: 'Handcrafted espressos, creamy lattes, and rich cappuccinos made from the finest beans.',
    cta: 'Explore Our Menu',
    ctaLink: '/services',
  },
  {
    image: '/images/hero-shake.png',
    tagline: 'Trending Now',
    title: 'Indulgent',
    titleAccent: 'Shakes',
    description: 'Oreo, KitKat, and signature shakes that are as Instagram-worthy as they are delicious.',
    cta: 'View Trendy Menu',
    ctaLink: '/services',
  },
  {
    image: '/images/hero-food.png',
    tagline: 'Fresh & Delicious',
    title: 'Quick Bites &',
    titleAccent: 'Sweet Treats',
    description: 'From margherita pizza to golden waffles — perfect accompaniments to your favorite drink.',
    cta: 'See All Items',
    ctaLink: '/services',
  },
  {
    image: '/images/hero-mojito.png',
    tagline: 'Cool & Refreshing',
    title: 'Signature',
    titleAccent: 'Coolers',
    description: 'Mint mojitos, fresh lime sodas, and detox waters to refresh your soul.',
    cta: 'View Drinks',
    ctaLink: '/services',
  },
  {
    image: '/images/hero-interior.png',
    tagline: 'Your Happy Place',
    title: 'Cozy Café',
    titleAccent: 'Vibes',
    description: 'A warm, welcoming space where every visit feels like coming home.',
    cta: 'Visit Us',
    ctaLink: '/contact',
  },
];



const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

/* ─── HERO SECTION COMPONENT ─── */
function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 5500);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  const slide = heroSlides[current];

  return (
    <section
      className="relative h-screen min-h-[700px] w-full overflow-hidden bg-espresso-950"
      id="hero-section"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background Images with Crossfade */}
      <AnimatePresence mode="sync">
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 z-0"
        >
          <img
            src={slide.image}
            alt=""
            className="w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>

      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-espresso-950/90 via-espresso-950/60 to-espresso-950/30" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-espresso-950/80 via-transparent to-espresso-950/30" />

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] z-[1] opacity-10">
        <svg viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="300" cy="300" r="280" stroke="#D4A853" strokeWidth="0.5" />
          <circle cx="300" cy="300" r="200" stroke="#D4A853" strokeWidth="0.3" />
          <circle cx="300" cy="300" r="120" stroke="#D4A853" strokeWidth="0.2" />
        </svg>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-amber-400/30"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.5,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container-custom w-full">
          <div className="max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Tagline */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex items-center gap-3 mb-6"
                >
                  <div className="w-10 h-px bg-amber-400" />
                  <span className="text-amber-400 text-sm font-semibold uppercase tracking-[0.3em]">
                    {slide.tagline}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-[0.95]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {slide.title}
                  <br />
                  <span className="text-gradient-gold">{slide.titleAccent}</span>
                </motion.h1>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.5 }}
                  className="text-white/70 text-lg md:text-xl leading-relaxed mb-10 max-w-lg"
                >
                  {slide.description}
                </motion.p>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.65 }}
                  className="flex flex-wrap gap-4"
                >
                  <Link to={slide.ctaLink} className="btn-primary text-base px-10 py-4">
                    {slide.cta}
                  </Link>
                  <Link
                    to="/about"
                    className="btn-secondary border-white/25 text-white hover:bg-white hover:text-espresso-900 text-base px-10 py-4"
                  >
                    Our Story
                  </Link>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`transition-all duration-500 rounded-full ${
              idx === current
                ? 'w-10 h-2 bg-amber-400'
                : 'w-2 h-2 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-amber-400/30 transition-all duration-300 border border-white/10 hover:border-amber-400/30 group"
        aria-label="Previous slide"
      >
        <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-amber-400/30 transition-all duration-300 border border-white/10 hover:border-amber-400/30 group"
        aria-label="Next slide"
      >
        <svg className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Slide Counter */}
      <div className="absolute bottom-10 right-8 z-10 text-white/40 text-sm font-mono">
        <span className="text-amber-400 font-bold text-lg">{String(current + 1).padStart(2, '0')}</span>
        <span className="mx-1">/</span>
        <span>{String(heroSlides.length).padStart(2, '0')}</span>
      </div>

      {/* Scroll Hint */}
      <motion.div
        className="absolute bottom-10 left-8 z-10 flex flex-col items-center gap-2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="text-white/40 text-xs uppercase tracking-widest rotate-90 origin-center translate-y-6">
          Scroll
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-amber-400/60 to-transparent" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  const [activeTab, setActiveTab] = useState('classic');
  const activeMenu = menuData[activeTab];

  return (
    <div className="bg-cream-50">
      {/* === HERO CAROUSEL SECTION === */}
      <HeroSection />

      {/* === FULL MENU SECTION === */}
      <section className="section-padding bg-cream-50 relative overflow-hidden" id="menu">
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.span variants={fadeUp} className="category-badge mb-4 inline-flex">
              <span>🍽️</span> Our Menu
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-4xl md:text-5xl font-bold text-espresso-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Explore Our <span className="text-gradient-gold">Offerings</span>
            </motion.h2>
          </motion.div>

          {/* Tab Navigation */}
          <div className="flex justify-center gap-2 mb-12 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeTab === tab.key
                    ? 'bg-espresso-900 text-white shadow-lg shadow-espresso-900/20'
                    : 'bg-white text-espresso-600 hover:bg-cream-200 shadow-sm'
                }`}
              >
                <span className="text-lg">{tab.emoji}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Menu Categories */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {activeMenu.categories.map((cat, idx) => (
                <div key={cat.name} className="mb-12 last:mb-0">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">{cat.icon}</span>
                    <h3
                      className="text-2xl md:text-3xl font-bold text-espresso-900"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {cat.name}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cat.items.map((item, itemIdx) => {
                      const whatsappMessage = encodeURIComponent(`Hi Squeeze N Sip, I would like to order: ${item.name} (₹${item.price})`);
                      const whatsappUrl = `https://wa.me/917993305226?text=${whatsappMessage}`;

                      return (
                        <motion.a
                          key={item.name}
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: itemIdx * 0.1 }}
                          className="group bg-white rounded-2xl overflow-hidden border border-espresso-100/50 hover:border-amber-400/30 hover:shadow-xl transition-all duration-500 flex items-center p-4 gap-4 cursor-pointer"
                        >
                          <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 relative">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            {/* WhatsApp overlay icon on hover */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.099.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.082 21.378c-1.464 0-2.898-.375-4.159-1.085l-4.667 1.225 1.246-4.549c-.779-1.3-1.189-2.774-1.189-4.298 0-5.161 4.197-9.359 9.359-9.359 5.161 0 9.358 4.197 9.358 9.359 0 5.162-4.197 9.359-9.359 9.359z" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-espresso-900 group-hover:text-amber-600 transition-colors">
                              {item.name}
                            </h4>
                            <p className="text-espresso-400 text-xs mt-1 line-clamp-2">{item.desc}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <p className="text-amber-600 font-bold text-sm">₹{item.price}</p>
                              <span className="text-[10px] uppercase font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200 opacity-0 group-hover:opacity-100 transition-opacity">Order</span>
                            </div>
                          </div>
                        </motion.a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* === CTA SECTION === */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero-interior.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-espresso-950/85" />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready for Your <span className="text-gradient-gold">Perfect Brew?</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-white/70 text-lg max-w-xl mx-auto mb-10">
              Visit us near Kartigi Bus Stop. We're open 8AM to 10PM, seven days a week.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary text-base px-10 py-4">
                📍 Find Us
              </Link>
              <a href="tel:7993305226" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-espresso-900 text-base px-10 py-4">
                📞 Call Now
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
